import { useState } from 'react';

interface CallScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function CallScheduleModal({ isOpen, onClose }: CallScheduleModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5boadpdn6dhfpiablt0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '' });
        setTimeout(() => {
          onClose();
          setSubmitStatus('idle');
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="relative max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        style={{ backgroundColor: '#0f0f0f', border: '1px solid rgba(251,191,36,0.15)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center text-white/40 hover:text-amber-400 transition-colors duration-300 cursor-pointer z-10"
          aria-label="Close"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {/* Header */}
        <div className="px-8 pt-10 pb-8" style={{ borderBottom: '1px solid rgba(251,191,36,0.1)' }}>
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-px bg-amber-400/50" />
            <span
              className="text-amber-400/70 text-xs font-medium tracking-[0.25em] uppercase"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              Get in touch
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div
              className="w-12 h-12 flex items-center justify-center flex-shrink-0"
              style={{ border: '1px solid rgba(251,191,36,0.3)', backgroundColor: 'rgba(251,191,36,0.05)' }}
            >
              <i className="ri-phone-line text-xl text-amber-400"></i>
            </div>
            <div>
              <h2
                className="text-3xl font-normal text-white leading-tight"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Schedule a <em className="text-amber-300 not-italic">Call</em>
              </h2>
              <p
                className="text-white/40 text-sm mt-1"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Let's discuss how we can work together
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="px-8 py-8" data-readdy-form id="call-schedule-form">
          <div className="space-y-5">

            {/* Name + Email row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label
                  htmlFor="name"
                  className="block text-xs font-medium text-white/50 mb-2 tracking-widest uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Full Name <span className="text-amber-400">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm text-white/90 placeholder-white/20 bg-transparent transition-all duration-300 focus:outline-none"
                  style={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    fontFamily: "'Inter', sans-serif"
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(251,191,36,0.5)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-xs font-medium text-white/50 mb-2 tracking-widest uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Email <span className="text-amber-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm text-white/90 placeholder-white/20 bg-transparent transition-all duration-300 focus:outline-none"
                  style={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    fontFamily: "'Inter', sans-serif"
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(251,191,36,0.5)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                  placeholder="your@email.com"
                />
              </div>
            </div>

            {/* Phone + Company row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label
                  htmlFor="phone"
                  className="block text-xs font-medium text-white/50 mb-2 tracking-widest uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Phone <span className="text-amber-400">*</span>
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm text-white/90 placeholder-white/20 bg-transparent transition-all duration-300 focus:outline-none"
                  style={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    fontFamily: "'Inter', sans-serif"
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(251,191,36,0.5)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div>
                <label
                  htmlFor="company"
                  className="block text-xs font-medium text-white/50 mb-2 tracking-widest uppercase"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                >
                  Company
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 text-sm text-white/90 placeholder-white/20 bg-transparent transition-all duration-300 focus:outline-none"
                  style={{
                    border: '1px solid rgba(255,255,255,0.1)',
                    fontFamily: "'Inter', sans-serif"
                  }}
                  onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(251,191,36,0.5)'; }}
                  onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                  placeholder="Your company name"
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label
                htmlFor="message"
                className="block text-xs font-medium text-white/50 mb-2 tracking-widest uppercase"
                style={{ fontFamily: "'Inter', sans-serif" }}
              >
                Message <span className="text-amber-400">*</span>
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={4}
                className="w-full px-4 py-3 text-sm text-white/90 placeholder-white/20 bg-transparent transition-all duration-300 resize-none focus:outline-none"
                style={{
                  border: '1px solid rgba(255,255,255,0.1)',
                  fontFamily: "'Inter', sans-serif"
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(251,191,36,0.5)'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                placeholder="Tell us about your partnership interests and goals..."
              />
              <p className="text-xs text-white/25 mt-1" style={{ fontFamily: "'Inter', sans-serif" }}>
                {formData.message.length}/500 characters
              </p>
            </div>

            {/* Submit Status */}
            {submitStatus === 'success' && (
              <div
                className="p-4 flex items-center gap-3"
                style={{ backgroundColor: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)' }}
              >
                <i className="ri-checkbox-circle-line text-amber-400 text-lg"></i>
                <p className="text-amber-300 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Thank you! We'll contact you shortly.
                </p>
              </div>
            )}

            {submitStatus === 'error' && (
              <div
                className="p-4 flex items-center gap-3"
                style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)' }}
              >
                <i className="ri-error-warning-line text-red-400 text-lg"></i>
                <p className="text-red-300 text-sm" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Something went wrong. Please try again.
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-amber-400 hover:bg-amber-300 text-black py-4 font-bold text-sm tracking-widest uppercase transition-all duration-300 hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Submitting...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  Schedule Call
                  <i className="ri-arrow-right-line"></i>
                </span>
              )}
            </button>
          </div>
        </form>

        {/* Contact Info Footer */}
        <div
          className="px-8 py-6"
          style={{ borderTop: '1px solid rgba(251,191,36,0.1)', backgroundColor: 'rgba(251,191,36,0.03)' }}
        >
          <p
            className="text-xs text-white/30 text-center mb-4 tracking-widest uppercase"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Prefer to reach out directly?
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <a
              href="tel:+905316148445"
              className="flex items-center gap-2 text-sm text-white/40 hover:text-amber-400 transition-colors duration-300 cursor-pointer"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <i className="ri-phone-line"></i>
              +90 531 614 8445
            </a>
            <a
              href="https://wa.me/message/OAKDCTBVIML6F1"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-sm text-white/40 hover:text-amber-400 transition-colors duration-300 cursor-pointer"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <i className="ri-whatsapp-line"></i>
              WhatsApp
            </a>
            <a
              href="mailto:info@plexusalliance.com"
              className="flex items-center gap-2 text-sm text-white/40 hover:text-amber-400 transition-colors duration-300 cursor-pointer"
              style={{ fontFamily: "'Inter', sans-serif" }}
            >
              <i className="ri-mail-line"></i>
              Email Us
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
